import { useRetainedRealtimeState } from '../../services/firebase/use-retained-realtime-state'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  mergeMessagePages,
  parseMessagePage,
  type MessagePage,
} from './conversations-pagination'
import {
  errorKind,
  type ConversationRepository,
} from './conversations-repository'
import type {
  Conversation,
  ConversationChannel,
  ThreadSummary,
} from './conversations-model'
import { resolveThread, type ThreadResolution } from './thread-resolution'

type ResolutionState = { status: 'idle' | 'loading' } | ThreadResolution

type MessageState = {
  owner: string | null
  status: 'idle' | 'loading' | 'ready' | 'forbidden' | 'error'
  newest: MessagePage | null
  historical: MessagePage[]
  loadingOlder: boolean
}

type SelectedThread = {
  threadId: string
  channel: ConversationChannel
}

export function useConversationThread(
  repository: ConversationRepository,
  tenantId: string | null,
  conversation: Conversation | null
) {
  const resolutionGeneration = useRef(0)
  const messageGeneration = useRef(0)
  const [resolution, setResolution] = useState<ResolutionState>({
    status: 'idle',
  })
  const [selectedThread, setSelectedThread] = useState<SelectedThread | null>(
    null
  )

  const patientId = conversation?.patientId ?? null
  const conversationTimestamp = conversation?.timestamp ?? null
  const conversationChannel = conversation?.channel ?? null
  const lastMessageCallId = conversation?.lastMessageCallId ?? null
  const lastMessageThreadId = conversation?.lastMessageThreadId ?? null

  useEffect(() => {
    const currentGeneration = ++resolutionGeneration.current
    setResolution({ status: patientId && tenantId ? 'loading' : 'idle' })
    setSelectedThread(null)
    if (
      !patientId ||
      conversationTimestamp === null ||
      !conversationChannel ||
      !tenantId
    ) {
      return
    }

    void resolveThread(repository, tenantId, {
      patientId,
      timestamp: conversationTimestamp,
      channel: conversationChannel,
      lastMessageCallId,
      lastMessageThreadId,
    }).then((result) => {
      if (resolutionGeneration.current !== currentGeneration) return
      setResolution(result)
      if (result.status === 'resolved') {
        setSelectedThread({
          threadId: result.threadId,
          channel: result.channel,
        })
      }
    })

    return () => {
      resolutionGeneration.current += 1
    }
  }, [
    conversationChannel,
    conversationTimestamp,
    lastMessageCallId,
    lastMessageThreadId,
    patientId,
    repository,
    tenantId,
  ])

  const owner =
    tenantId && patientId && selectedThread
      ? `${tenantId}:${patientId}:${selectedThread.threadId}`
      : null

  const [messageState, setMessageState, resumeMessages] =
    useRetainedRealtimeState<MessageState>(
      [
        'messages',
        tenantId,
        patientId,
        selectedThread?.threadId ?? null,
        selectedThread?.channel ?? null,
      ],
      {
        owner,
        status: owner ? 'loading' : 'idle',
        newest: null,
        historical: [],
        loadingOlder: false,
      },
      (value) => value.status === 'ready'
    )

  useEffect(() => {
    const currentGeneration = ++messageGeneration.current
    resumeMessages()
    setMessageState((current) => ({ ...current, loadingOlder: false }))
    if (!tenantId || !patientId || !selectedThread || !owner) return

    const unsubscribe = repository.subscribeMessages(
      tenantId,
      patientId,
      selectedThread.threadId,
      (value) => {
        if (messageGeneration.current !== currentGeneration) return
        setMessageState((current) => ({
          ...current,
          status: 'ready',
          newest: parseMessagePage(value, selectedThread.channel),
        }))
      },
      (error) => {
        if (messageGeneration.current !== currentGeneration) return
        setMessageState((current) => ({
          ...current,
          status: errorKind(error) === 'forbidden' ? 'forbidden' : 'error',
          newest: null,
          historical: [],
        }))
      }
    )

    return () => {
      messageGeneration.current += 1
      unsubscribe()
    }
  }, [
    owner,
    patientId,
    repository,
    selectedThread,
    tenantId,
    resumeMessages,
    setMessageState,
  ])

  const effectiveMessages =
    messageState.owner === owner
      ? messageState
      : {
          owner,
          status: owner ? ('loading' as const) : ('idle' as const),
          newest: null,
          historical: [],
          loadingOlder: false,
        }
  const messages = useMemo(
    () =>
      mergeMessagePages(effectiveMessages.newest, effectiveMessages.historical),
    [effectiveMessages.historical, effectiveMessages.newest]
  )
  const lastPage =
    effectiveMessages.historical.at(-1) ?? effectiveMessages.newest ?? null

  const loadOlder = useCallback(async () => {
    if (
      !tenantId ||
      !patientId ||
      !selectedThread ||
      !lastPage?.hasOlder ||
      !lastPage.cursor ||
      effectiveMessages.loadingOlder
    ) {
      return
    }
    const requestGeneration = messageGeneration.current
    setMessageState((current) => ({ ...current, loadingOlder: true }))
    try {
      const value = await repository.getOlderMessages(
        tenantId,
        patientId,
        selectedThread.threadId,
        lastPage.cursor
      )
      if (messageGeneration.current !== requestGeneration) return
      const page = parseMessagePage(value, selectedThread.channel)
      setMessageState((current) => ({
        ...current,
        historical: [...current.historical, page],
        loadingOlder: false,
      }))
    } catch (error) {
      if (messageGeneration.current !== requestGeneration) return
      setMessageState((current) => ({
        ...current,
        status: errorKind(error) === 'forbidden' ? 'forbidden' : 'error',
        loadingOlder: false,
      }))
    }
  }, [
    effectiveMessages.loadingOlder,
    lastPage,
    patientId,
    repository,
    selectedThread,
    tenantId,
    setMessageState,
  ])

  const chooseThread = useCallback((thread: ThreadSummary) => {
    setSelectedThread({
      threadId: thread.threadId,
      channel: thread.channel,
    })
  }, [])

  const clearThreadChoice = useCallback(() => setSelectedThread(null), [])

  return {
    resolution,
    selectedThread,
    chooseThread,
    clearThreadChoice,
    messageStatus: effectiveMessages.status,
    messages,
    hasOlderMessages: lastPage?.hasOlder ?? false,
    loadingOlderMessages: effectiveMessages.loadingOlder,
    loadOlderMessages: loadOlder,
    messageOwner: owner,
  }
}
